import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "styled-components";

import type { SyncState } from "../../stores/SettingsSync";
import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Button, SmallHeading } from "../ui/controls";
import { SettingNote, SettingsGroup } from "./settingsStyles";
import { SignInDialog } from "./SignInDialog";

/** How saving the settings on the share server is going, in words. */
const SYNC_STATES: Record<SyncState, string> = {
    off: "Your settings aren't saved on the share server yet.",
    saving: "Saving your settings on the share server…",
    saved: "Your settings are saved on the share server, for every device you sign in on.",
    offline: "No connection. Your settings are saved on the share server as soon as there is one.",
    failed: "Your settings couldn't be saved on the share server.",
};

/**
 * Who's signed in on this device. Signed in, the settings are kept on the share server, so every device someone
 * signs in on looks and works the same. Text size stays per device, as it depends on the screen.
 */
export const AccountSettings = observer(function AccountSettings() {
    const { account, settingsSync } = useStores();
    const [signingIn, setSigningIn] = useState(false);
    const [problem, setProblem] = useState<string>();
    const signedIn = account.account;

    async function signOut() {
        setProblem(undefined);

        try {
            await account.signOut();
        } catch (error) {
            setProblem(error instanceof Error ? error.message : String(error));
        }
    }

    return (
        <SettingsGroup>
            <SmallHeading>Account</SmallHeading>
            {signedIn ? (
                <>
                    <Who>
                        <Name>{signedIn.name}</Name>
                        <Email>{signedIn.email}</Email>
                    </Who>
                    <SettingNote>
                        {SYNC_STATES[settingsSync.state]} {settingsSync.state === "failed" && settingsSync.problem}
                    </SettingNote>
                    <Actions>
                        {settingsSync.state !== "saved" && <Button onClick={() => void settingsSync.sync()}>Try again</Button>}
                        <Button $kind="danger" onClick={signOut}>
                            Sign out on this device
                        </Button>
                    </Actions>
                    {problem && <SettingNote role="alert">{problem}</SettingNote>}
                </>
            ) : (
                <>
                    <SettingNote>
                        Sign in to keep your settings on the share server, so every device you sign in on looks and works the same. Text
                        size stays per device, as it depends on the screen.
                    </SettingNote>
                    <SignIn $kind="primary" onClick={() => setSigningIn(true)}>
                        Sign in
                    </SignIn>
                </>
            )}
            {signingIn && <SignInDialog onClose={() => setSigningIn(false)} />}
        </SettingsGroup>
    );
});

const Who = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const Name = styled.span`
    font-size: 16px;
    font-weight: 600;
`;

const Email = styled.span`
    font-size: 14px;
    color: ${color.textMuted};
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const SignIn = styled(Button)`
    align-self: flex-start;
`;
